import type { GamePreset, OcrModelResource } from '@/presets/types'

export type OcrModelKey = 'detection' | 'recognition'

export interface OcrCacheProgress {
  readonly model: OcrModelKey
  readonly progress: number
  readonly fromCache: boolean
}

export const OCR_MODEL_CACHE_NAME = 'maple-log-ocr-models-v3'
const MODEL_VERSION_PARAM = '__maple_log_model_version'

export interface ResolvedOcrModels {
  readonly models: GamePreset['remoteResources']['ocrModels']
  release(): void
}

export async function ensureOcrModelsCached(
  models: GamePreset['remoteResources']['ocrModels'],
  onProgress: (progress: OcrCacheProgress) => void,
): Promise<void> {
  const resources: readonly [OcrModelKey, OcrModelResource][] = [
    ['detection', models.detection],
    ['recognition', models.recognition],
  ]
  const cache = 'caches' in window ? await caches.open(OCR_MODEL_CACHE_NAME) : null

  for (let index = 0; index < resources.length; index += 1) {
    const [model, resource] = resources[index]!
    const cacheRequest = createModelCacheRequest(resource)
    const cached = await cache?.match(cacheRequest)
    if (cached) {
      onProgress({ model, progress: (index + 1) / resources.length, fromCache: true })
      continue
    }

    await downloadModelResource(resource, cache, cacheRequest, (resourceProgress) => {
      onProgress({
        model,
        progress: (index + resourceProgress) / resources.length,
        fromCache: false,
      })
    })
  }

  if (cache) await removeOutdatedModels(cache, resources.map(([, resource]) => modelCacheKey(resource)))
}

export async function resolveCachedOcrModels(
  models: GamePreset['remoteResources']['ocrModels'],
): Promise<ResolvedOcrModels> {
  if (!('caches' in window) || typeof URL.createObjectURL !== 'function') {
    return { models, release() {} }
  }

  const cache = await caches.open(OCR_MODEL_CACHE_NAME)
  const objectUrls: string[] = []
  const resolved: Partial<Record<OcrModelKey, OcrModelResource>> = {}
  const resources: readonly [OcrModelKey, OcrModelResource][] = [
    ['detection', models.detection],
    ['recognition', models.recognition],
  ]

  for (const [key, resource] of resources) {
    const response = await cache.match(createModelCacheRequest(resource))
    if (!response) {
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
      return { models, release() {} }
    }
    const objectUrl = URL.createObjectURL(await response.blob())
    objectUrls.push(objectUrl)
    resolved[key] = { ...resource, url: objectUrl }
  }

  return {
    models: {
      detection: resolved.detection!,
      recognition: resolved.recognition!,
    },
    release() {
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    },
  }
}

export function modelCacheKey(resource: OcrModelResource): string {
  const url = new URL(resource.url, window.location.href)
  url.searchParams.set(MODEL_VERSION_PARAM, resource.version)
  return url.href
}

function createModelCacheRequest(resource: OcrModelResource): Request {
  return new Request(modelCacheKey(resource), { credentials: 'omit' })
}

async function downloadModelResource(
  resource: OcrModelResource,
  cache: Cache | null,
  cacheRequest: Request,
  onProgress: (progress: number) => void,
): Promise<void> {
  const urls = [resource.url, ...(resource.fallbackUrls ?? [])]
  const failures: string[] = []
  let reportedProgress = 0

  for (const url of urls) {
    let response: Response
    try {
      response = await fetch(new Request(url, {
        cache: 'no-store',
        credentials: 'omit',
        mode: 'cors',
      }))
    } catch (error) {
      failures.push(`${url}: ${errorMessage(error)}`)
      continue
    }

    if (!response.ok) {
      failures.push(`${url}: HTTP ${response.status}`)
      continue
    }

    const cacheWrite = cache
      ? cache.put(cacheRequest, response.clone()).then(
          () => ({ ok: true as const }),
          (error: unknown) => ({ ok: false as const, error }),
        )
      : null

    try {
      await consumeResponse(response, (progress) => {
        reportedProgress = Math.max(reportedProgress, progress)
        onProgress(reportedProgress)
      })
    } catch (error) {
      if (cacheWrite) await cacheWrite
      await cache?.delete(cacheRequest)
      failures.push(`${url}: ${errorMessage(error)}`)
      continue
    }

    const cacheResult = await cacheWrite
    if (cacheResult && !cacheResult.ok) {
      await cache?.delete(cacheRequest)
      throw new Error(`OCR model cache write failed: ${errorMessage(cacheResult.error)}`)
    }
    return
  }

  throw new Error(`OCR resource request failed (${failures.join('; ')})`)
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'network error'
}

async function consumeResponse(response: Response, onProgress: (progress: number) => void): Promise<void> {
  const total = Number(response.headers.get('content-length')) || 0
  if (!response.body) {
    await response.arrayBuffer()
    onProgress(1)
    return
  }

  const reader = response.body.getReader()
  let loaded = 0
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    loaded += value.byteLength
    if (total > 0) onProgress(Math.min(1, loaded / total))
  }
  onProgress(1)
}

async function removeOutdatedModels(cache: Cache, currentUrls: readonly string[]): Promise<void> {
  const current = new Set(currentUrls.map((url) => new URL(url, window.location.href).href))
  const requests = await cache.keys()
  await Promise.all(requests
    .filter((request) => !current.has(request.url))
    .map((request) => cache.delete(request)))
}
